"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { Eye, EyeOff, Check, Loader2 } from "lucide-react";
import svgPaths from "@/assets/svg/svg-82k28lxhj4";
import { img5 } from "@/assets/svg/svg-6ajzz";
import helpSvgPaths from "@/assets/svg/svg-nbyaumhabe";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import img from "@/assets/images/landing/27a8497d5cb712a39a846f3d49443b8283852dca.png";
import img1 from "@/assets/images/landing/1173f0775d4f6c2c6c7caf725e2ec6d6ed6aca1d.png";
import img2 from "@/assets/images/landing/572a6183892ca2df6e032e2c3c86f59b0f2caa70.png";
import img3 from "@/assets/images/landing/e315784949efca620db678761bac77b9ff2a218a.png";
import img4 from "@/assets/images/landing/fcd7e60494e98ef4a34b3228a1783b7f500b0df6.png";
import type { StaticImageData } from "next/image";
import styles from "@/app/signup/page.module.css";
import { toast } from "sonner";
import { AnimatePresence } from "motion/react";
import OTPInput from "@/components/ui/OTPInput";

// Validation helper functions
const validateEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

const validateName = (name: string): boolean => {
	return name.length >= 2;
};

const validatePassword = (password: string): boolean => {
	return password.length >= 8;
};

interface FormErrors {
	name?: string;
	email?: string;
	password?: string;
}

function BackgroundOverlay() {
	return (
		<div
			className="absolute bg-[#142a1e] inset-0 overflow-hidden"
			data-name="Background overlay"
		>
			<div
				className="absolute right-0 bottom-0 w-[31.25rem] overflow-hidden"
				data-name="Hero Image Right"
				style={{
					margin: 0,
					padding: 0,
					height: "calc(300px + 4rem)",
					marginBottom: "-4rem",
				}}
			>
				<div className="relative w-full h-full">
					<Image
						alt=""
						src={imgHeroImageRight}
						fill
						className="max-w-none object-right-bottom object-cover pointer-events-none"
						sizes="31.25rem"
						style={{
							objectPosition: "right bottom",
							margin: 0,
							padding: 0,
							display: "block",
						}}
					/>
				</div>
			</div>
		</div>
	);
}

function Stars() {
	return (
		<div className="relative shrink-0 size-[5rem]" data-name="Stars">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 80 80"
			>
				<g id="Stars">
					<path
						clipRule="evenodd"
						d={svgPaths.p27c0b100}
						fill="var(--fill-0, #FEC84B)"
						fillRule="evenodd"
						id="Star"
					/>
					<path
						clipRule="evenodd"
						d={svgPaths.p3a5e2f00}
						fill="var(--fill-0, #FEC84B)"
						fillRule="evenodd"
						id="Star_2"
					/>
					<g id="villa">
						<mask
							height="63"
							id="mask0_19_10696"
							maskUnits="userSpaceOnUse"
							style={{ maskType: "alpha" }}
							width="63"
							x="8"
							y="8"
						>
							<rect
								fill="var(--fill-0, #D9D9D9)"
								height="62"
								id="Bounding box"
								width="62"
								x="8.5"
								y="8.5"
							/>
						</mask>
						<g mask="url(#mask0_19_10696)">
							<path
								d={svgPaths.p26664a00}
								fill="var(--fill-0, #A7FF5E)"
								id="villa_2"
							/>
						</g>
					</g>
				</g>
			</svg>
		</div>
	);
}

function TextAndSupportingText() {
	return (
		<div
			className="content-stretch flex flex-col gap-[1.5rem] items-center relative shrink-0 w-full"
			data-name="Text and supporting text"
			style={{ width: "100%", textAlign: "left", marginTop: "auto" }}
		>
			<p
				className="font-['Bricolage_Grotesque:SemiBold',sans-serif] font-semibold leading-[4.875rem] relative shrink-0 text-[3.75rem] text-white tracking-[0.075rem] w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Smarter Tax Lien Property Research for Profitable Investments
			</p>
			<p
				style={{ width: "80%", textAlign: "left" }}
				className="font-['Inter:Medium',sans-serif] font-medium leading-[1.875rem] not-italic relative shrink-0 text-[#8c99a7] text-[1.25rem] w-full"
			>
				{`Transform hours of property research into minutes of actionable decisions. `}
			</p>
		</div>
	);
}

function Avatar({
	src,
	bgColor,
}: {
	src: string | StaticImageData;
	bgColor: string;
}) {
	return (
		<div
			className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
			data-name="Avatar"
		>
			<div
				aria-hidden="true"
				className="absolute inset-0 pointer-events-none rounded-[12.5rem] relative"
			>
				<div
					className={`absolute ${bgColor} inset-0 rounded-[12.5rem]`}
				/>
				<Image
					alt=""
					src={src}
					fill
					className="max-w-none object-center object-cover rounded-[12.5rem]"
				/>
			</div>
			<div
				aria-hidden="true"
				className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
			/>
			<div
				className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
				data-name="_Avatar contrast inner border"
			>
				<div
					aria-hidden="true"
					className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
				/>
			</div>
		</div>
	);
}

function Avatars() {
	return (
		<div
			className="box-border content-stretch flex items-start pl-0 pr-[0.75rem] py-0 relative shrink-0"
			data-name="Avatars"
		>
			<Avatar src={img} bgColor="bg-[#cfcbdc]" />
			<Avatar src={img1} bgColor="bg-[#d6cfb7]" />
			<Avatar src={img2} bgColor="bg-[#d7e3e8]" />
			<Avatar src={img3} bgColor="bg-[#dadcd6]" />
			<Avatar src={img4} bgColor="bg-[#d9d0e6]" />
		</div>
	);
}

function Star() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_19_10687)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_19_10687">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Stars1() {
	return (
		<div
			className="content-stretch flex gap-[0.25rem] items-center relative shrink-0"
			data-name="Stars"
		>
			{[...Array(5)].map((_, i) => (
				<div
					key={i}
					className="overflow-clip relative shrink-0 size-[1.25rem]"
					data-name="Star icon"
				>
					<div
						className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
						data-name="Star background"
					>
						<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
							<svg
								className="block size-full"
								fill="none"
								preserveAspectRatio="none"
								viewBox="0 0 18 17"
							>
								<path
									d={svgPaths.p30303900}
									fill="var(--fill-0, #F2F4F7)"
									id="Star background"
								/>
							</svg>
						</div>
					</div>
					<Star />
				</div>
			))}
		</div>
	);
}

function Row() {
	return (
		<div
			className="content-stretch flex gap-[0.5rem] items-center relative shrink-0"
			data-name="Row"
		>
			<Stars1 />
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[1.5rem] not-italic relative shrink-0 text-[1rem] text-nowrap text-white whitespace-pre">
				5.0
			</p>
		</div>
	);
}

function Reviews() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.25rem] items-start relative shrink-0"
			data-name="Reviews"
		>
			<Row />
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-[1.5rem] not-italic relative shrink-0 text-[#8c99a7] text-[1rem] text-nowrap whitespace-pre">
				from 50+ investors
			</p>
		</div>
	);
}

function Row1() {
	return (
		<div
			className="content-stretch flex gap-[1rem] items-center relative shrink-0"
			data-name="Row"
		>
			<div
				className="content-stretch flex gap-[0.5rem] items-start relative shrink-0"
				data-name="Avatar group"
			>
				<Avatars />
			</div>
			<Reviews />
		</div>
	);
}

function Content() {
	return (
		<div
			className="absolute content-stretch flex flex-col gap-[3rem] items-start left-[5.0625rem] top-[5rem] w-[80%]"
			data-name="Content"
		>
			<Stars />
			<TextAndSupportingText />
			<Row1 />
		</div>
	);
}

function Section() {
	return (
		<div
			className="basis-0 grow h-screen min-h-screen min-w-px relative shrink-0 section-signup"
			data-name="Section"
			style={{
				flexDirection: "column",
				flex: 1,
				maxWidth: "65%",
				alignItems: "flex-start",
			}}
		>
			<BackgroundOverlay />
			<Content />
		</div>
	);
}

function TextAndSupportingText1() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 w-full"
			data-name="Text and supporting text"
		>
			<p
				className={`font-['Inter:Semi_Bold',sans-serif] font-semibold relative shrink-0 text-[#101828] w-full ${styles.signupTitle}`}
			>
				Sign up
			</p>
			<p
				className={`font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[#475467] w-full ${styles.signupSubtitle}`}
			>
				Start your 14-day free trial.
			</p>
		</div>
	);
}

function Group() {
	return (
		<div
			className="inset-0 mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[0rem] mask-size-[1.5rem_1.5rem]"
			data-name="Group"
			style={{ maskImage: `url('${img5}')` }}
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 24 24"
			>
				<g id="Group">
					<path
						d={svgPaths.p39c85400}
						fill="var(--fill-0, #F1511B)"
						id="Vector"
					/>
					<path
						d={svgPaths.p14f3a7f0}
						fill="var(--fill-0, #80CC28)"
						id="Vector_2"
					/>
					<path
						d={svgPaths.p1cf9b200}
						fill="var(--fill-0, #00ADEF)"
						id="Vector_3"
					/>
					<path
						d={svgPaths.p3dbbfc80}
						fill="var(--fill-0, #FBBC09)"
						id="Vector_4"
					/>
				</g>
			</svg>
		</div>
	);
}

function ClipPathGroup() {
	return (
		<div className="absolute contents inset-0" data-name="Clip path group">
			<Group />
		</div>
	);
}

function SocialButtons() {
	const handleGoogleSignUp = () => {
		import("@/services/cognito").then(({ signInWithProvider }) =>
			signInWithProvider("Google")
		);
	};

	const handleMicrosoftSignUp = () => {
		import("@/services/cognito").then(({ signInWithProvider }) =>
			signInWithProvider("Microsoft")
		);
	};

	return (
		<div className="content-stretch flex flex-col gap-[1.5rem] items-start relative shrink-0 w-full">
			{/* Google Sign Up */}
			<div
				className="content-stretch flex flex-col gap-[0.75rem] items-center justify-center relative shrink-0 w-full"
				data-name="Social button groups"
			>
				<button
					type="button"
					onClick={handleGoogleSignUp}
					className="bg-white relative rounded-[0.75rem] shrink-0 w-full transition-transform duration-200 hover:scale-[1.02] cursor-pointer"
					data-name="Social button"
					data-testid="signup-google-button"
				>
					<div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
						<div className="box-border content-stretch flex gap-[0.75rem] items-center justify-center px-[1rem] py-[0.625rem] relative w-full">
							<div
								className="overflow-clip relative shrink-0 size-[1.5rem]"
								data-name="Social icon"
							>
								<div
									className="absolute inset-[40.99%_0.97%_12.07%_51%]"
									data-name="Vector"
								>
									<svg
										className="block size-full"
										fill="none"
										preserveAspectRatio="none"
										viewBox="0 0 12 12"
									>
										<path
											d={svgPaths.p29993c00}
											fill="var(--fill-0, #4285F4)"
											id="Vector"
										/>
									</svg>
								</div>
								<div
									className="absolute bottom-0 left-[6.32%] right-[15.86%] top-[59.59%]"
									data-name="Vector"
								>
									<svg
										className="block size-full"
										fill="none"
										preserveAspectRatio="none"
										viewBox="0 0 19 10"
									>
										<path
											d={svgPaths.p3e923d00}
											fill="var(--fill-0, #34A853)"
											id="Vector"
										/>
									</svg>
								</div>
								<div
									className="absolute inset-[27.56%_77.07%_27.54%_1%]"
									data-name="Vector"
								>
									<svg
										className="block size-full"
										fill="none"
										preserveAspectRatio="none"
										viewBox="0 0 6 11"
									>
										<path
											d={svgPaths.p18a6ed00}
											fill="var(--fill-0, #FBBC04)"
											id="Vector"
										/>
									</svg>
								</div>
								<div
									className="absolute bottom-[59.56%] left-[6.32%] right-[15.54%] top-0"
									data-name="Vector"
								>
									<svg
										className="block size-full"
										fill="none"
										preserveAspectRatio="none"
										viewBox="0 0 19 10"
									>
										<path
											d={svgPaths.p2d291200}
											fill="var(--fill-0, #EA4335)"
											id="Vector"
										/>
									</svg>
								</div>
							</div>
							<p
								className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic relative shrink-0 text-[#12301b] text-nowrap whitespace-pre ${styles.signupButtonText}`}
							>
								Sign up with Google
							</p>
							<div
								aria-hidden="true"
								className={`absolute border border-[#dde3e9] border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
							/>
						</div>
					</div>
				</button>
			</div>

			{/* Microsoft Sign Up */}
			<div
				className="content-stretch flex flex-col gap-[0.75rem] items-center justify-center relative shrink-0 w-full"
				data-name="Social button groups"
			>
				<button
					type="button"
					onClick={handleMicrosoftSignUp}
					className="bg-white relative rounded-[0.75rem] shrink-0 w-full transition-transform duration-200 hover:scale-[1.02] cursor-pointer"
					data-name="Social button"
					data-testid="signup-microsoft-button"
				>
					<div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
						<div className="box-border content-stretch flex gap-[0.75rem] items-center justify-center px-[1rem] py-[0.625rem] relative w-full">
							<div
								className="overflow-clip relative shrink-0 size-[1.5rem]"
								data-name="Social icon"
							>
								<ClipPathGroup />
							</div>
							<p
								className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic relative shrink-0 text-[#12301b] text-nowrap whitespace-pre ${styles.signupButtonText}`}
							>
								Sign up with Microsoft
							</p>
							<div
								aria-hidden="true"
								className={`absolute border border-[#dde3e9] border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
							/>
						</div>
					</div>
				</button>
			</div>
		</div>
	);
}

function Divider() {
	return (
		<div className="content-stretch flex gap-[1rem] items-center relative shrink-0 w-full">
			<p
				className={`font-['Inter:Bold',sans-serif] font-bold not-italic relative shrink-0 text-[#12301b] text-nowrap uppercase whitespace-pre ${styles.signupDividerText}`}
			>
				Sign up with email
			</p>
			<div className="basis-0 bg-[#d9d9d9] grow h-px min-h-px min-w-px shrink-0" />
		</div>
	);
}

function SignUpForm({
	onOTPRequired,
}: {
	onOTPRequired: (email: string) => void;
}) {
	const [showPassword, setShowPassword] = useState(false);
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
	});
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});
	const [submitting, setSubmitting] = useState(false);
	const [generalError, setGeneralError] = useState("");

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));

		// Clear error when user starts typing
		if (errors[name as keyof FormErrors]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }));
		}
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setTouched((prev) => ({ ...prev, [name]: true }));

		// Validate on blur
		let error: string | undefined;
		if (name === "name" && !validateName(value)) {
			error = "Name must be at least 2 characters";
		} else if (name === "email" && !validateEmail(value)) {
			error = "Invalid email address";
		} else if (name === "password" && !validatePassword(value)) {
			error = "Password must be at least 8 characters";
		}

		if (error) {
			setErrors((prev) => ({ ...prev, [name]: error }));
		}
	};

	interface CognitoError {
		code?: string;
		name?: string;
		message?: string;
	}

	const getErrorMessage = (error: unknown): string => {
		const cognitoError = error as CognitoError;
		const code = cognitoError?.code || cognitoError?.name;
		const message = cognitoError?.message || "";

		switch (code) {
			case "UsernameExistsException":
				return "An account with this email already exists. Please sign in instead.";
			case "InvalidPasswordException":
				return "Password does not meet requirements. Please use a stronger password.";
			case "InvalidParameterException":
				return (
					message ||
					"Invalid information provided. Please check your details."
				);
			case "TooManyRequestsException":
				return "Too many signup attempts. Please try again later.";
			default:
				if (message) {
					if (
						message.includes("already exists") ||
						message.includes("taken")
					) {
						return "An account with this email already exists.";
					}
				}
				return "Signup failed. Please try again.";
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setGeneralError("");

		// Validate all fields
		const newErrors: FormErrors = {};
		if (!validateName(formData.name)) {
			newErrors.name = "Name must be at least 2 characters";
		}
		if (!validateEmail(formData.email)) {
			newErrors.email = "Invalid email address";
		}
		if (!validatePassword(formData.password)) {
			newErrors.password = "Password must be at least 8 characters";
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			setTouched({ name: true, email: true, password: true });
			return;
		}

		try {
			setSubmitting(true);
			setGeneralError("");
			const { signupCognito } = await import("@/services/cognito");
			const response = await signupCognito({
				username: formData.email,
				email: formData.email,
				password: formData.password,
				name: formData.name,
			});

			// Check if user needs to confirm with OTP
			if (response && !response.userConfirmed) {
				toast.success("Account created! Please verify your email.", {
					description: `We've sent a verification code to ${formData.email}`,
				});
				onOTPRequired(formData.email);
			} else {
				toast.success("Account created successfully!");
				window.location.href = "/login";
			}
		} catch (error: unknown) {
			const errorMessage = getErrorMessage(error);
			setGeneralError(errorMessage);
			toast.error(errorMessage);
		} finally {
			setSubmitting(false);
		}
	};

	const isEmailValid =
		formData.email &&
		!errors.email &&
		touched.email &&
		validateEmail(formData.email);

	return (
		<form
			onSubmit={handleSubmit}
			className="content-stretch flex flex-col gap-[1.25rem] items-start relative shrink-0 w-full"
			data-name="Form"
		>
			{/* Name Input */}
			<div
				className="box-border content-stretch flex flex-col gap-[0.375rem] items-start overflow-visible p-0 relative shrink-0 w-full"
				data-name="Input field"
			>
				<div
					className="content-stretch flex flex-col gap-[0.375rem] items-start relative shrink-0 w-full"
					data-name="Input with label"
				>
					<p
						className={`font-['Inter:Medium',sans-serif] font-medium not-italic relative shrink-0 text-[#12301b] text-nowrap whitespace-pre ${styles.signupLabelText}`}
						data-testid="signup-name-label"
					>
						Name*
					</p>
					<div
						className="bg-white relative rounded-[0.75rem] shrink-0 w-full"
						data-name="Input"
					>
						<div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
							<div className="box-border content-stretch flex gap-[0.5rem] items-center px-[0.875rem] py-[0.625rem] relative w-full">
								<input
									id="name"
									name="name"
									type="text"
									placeholder="Enter your name"
									data-testid="signup-name-input"
									className={`basis-0 font-['Inter:Regular',sans-serif] font-normal grow min-h-px min-w-px not-italic relative shrink-0 text-[#101828] bg-transparent border-none outline-none placeholder:text-[#697b8e] ${styles.signupInputText}`}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formData.name}
								/>
							</div>
						</div>
						<div
							aria-hidden="true"
							className={`absolute border ${
								touched.name && errors.name
									? "border-red-500"
									: "border-[#dde3e9]"
							} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
						/>
					</div>
					{touched.name && errors.name && (
						<p
							className={`font-['Inter:Regular',sans-serif] font-normal not-italic text-red-500 ${styles.signupErrorText}`}
							data-testid="signup-name-error"
						>
							{errors.name}
						</p>
					)}
				</div>
			</div>

			{/* Email Input */}
			<div
				className="box-border content-stretch flex flex-col gap-[0.375rem] items-start overflow-visible p-0 relative shrink-0 w-full"
				data-name="Input field"
			>
				<div
					className="content-stretch flex flex-col gap-[0.375rem] items-start relative shrink-0 w-full"
					data-name="Input with label"
				>
					<p
						className={`font-['Inter:Medium',sans-serif] font-medium not-italic relative shrink-0 text-[#12301b] text-nowrap whitespace-pre ${styles.signupLabelText}`}
						data-testid="signup-email-label"
					>
						Email*
					</p>
					<div
						className="bg-white relative rounded-[0.75rem] shrink-0 w-full"
						data-name="Input"
					>
						<div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
							<div className="box-border content-stretch flex gap-[0.5rem] items-center px-[0.875rem] py-[0.625rem] relative w-full">
								<input
									id="email"
									name="email"
									type="email"
									placeholder="Enter your email"
									data-testid="signup-email-input"
									className={`basis-0 font-['Inter:Regular',sans-serif] font-normal grow min-h-px min-w-px not-italic relative shrink-0 text-[#101828] bg-transparent border-none outline-none placeholder:text-[#697b8e] ${styles.signupInputText}`}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formData.email}
								/>
								{isEmailValid && (
									<motion.div
										initial={{ scale: 0, opacity: 0 }}
										animate={{ scale: 1, opacity: 1 }}
										transition={{
											duration: 0.3,
											ease: "easeOut",
										}}
									>
										<Check className="w-5 h-5 text-green-600" />
									</motion.div>
								)}
							</div>
						</div>
						<div
							aria-hidden="true"
							className={`absolute border ${
								touched.email && errors.email
									? "border-red-500"
									: isEmailValid
										? "border-green-500"
										: "border-[#dde3e9]"
							} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
						/>
					</div>
					{touched.email && errors.email && (
						<p
							className={`font-['Inter:Regular',sans-serif] font-normal not-italic text-red-500 ${styles.signupErrorText}`}
							data-testid="signup-email-error"
						>
							{errors.email}
						</p>
					)}
				</div>
			</div>

			{/* Password Input */}
			<div
				className="box-border content-stretch flex flex-col gap-[0.375rem] items-start overflow-visible p-0 relative shrink-0 w-full"
				data-name="Input field"
			>
				<div
					className="content-stretch flex flex-col gap-[0.375rem] items-start relative shrink-0 w-full"
					data-name="Input with label"
				>
					<p
						className={`font-['Inter:Medium',sans-serif] font-medium not-italic relative shrink-0 text-[#12301b] text-nowrap whitespace-pre ${styles.signupLabelText}`}
						data-testid="signup-password-label"
					>
						Password*
					</p>
					<div
						className="bg-white relative rounded-[0.75rem] shrink-0 w-full"
						data-name="Input"
					>
						<div className="flex flex-row items-center overflow-clip rounded-[inherit] size-full">
							<div className="box-border content-stretch flex gap-[0.5rem] items-center px-[0.875rem] py-[0.625rem] relative w-full">
								<input
									id="password"
									name="password"
									type={showPassword ? "text" : "password"}
									placeholder="Create a password"
									data-testid="signup-password-input"
									className={`basis-0 font-['Inter:Regular',sans-serif] font-normal grow min-h-px min-w-px not-italic relative shrink-0 text-[#101828] bg-transparent border-none outline-none placeholder:text-[#697b8e] ${styles.signupInputText}`}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formData.password}
								/>
								<button
									type="button"
									onClick={() =>
										setShowPassword(!showPassword)
									}
									className="shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
									data-testid="signup-password-toggle"
								>
									{showPassword ? (
										<EyeOff className="w-5 h-5 text-[#697b8e]" />
									) : (
										<Eye className="w-5 h-5 text-[#697b8e]" />
									)}
								</button>
							</div>
						</div>
						<div
							aria-hidden="true"
							className={`absolute border ${
								touched.password && errors.password
									? "border-red-500"
									: "border-[#dde3e9]"
							} border-solid inset-0 pointer-events-none rounded-[0.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`}
						/>
					</div>
					<p
						className={`font-['Inter:Regular',sans-serif] font-normal not-italic relative shrink-0 text-[#475467] w-full ${styles.signupHelperText}`}
						data-testid="signup-password-error"
					>
						{touched.password && errors.password ? (
							<span className="text-red-500">
								{errors.password}
							</span>
						) : (
							"Must be at least 8 characters."
						)}
					</p>
				</div>
			</div>

			{/* General Error Message */}
			{generalError && (
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -10 }}
					className="w-full p-3 bg-red-50 border border-red-200 rounded-lg"
				>
					<p className="text-sm text-red-600 font-medium">
						{generalError}
					</p>
				</motion.div>
			)}

			{/* Submit Button */}
			<div
				className="content-stretch flex flex-col gap-[1rem] items-start relative shrink-0 w-full"
				data-name="Actions"
			>
				<motion.button
					type="submit"
					disabled={submitting}
					className={`bg-[#cbf7a6] h-[3.25rem] relative rounded-[2.25rem] shrink-0 w-full ${
						submitting
							? "opacity-70 cursor-not-allowed"
							: "cursor-pointer"
					}`}
					data-name="Button"
					data-testid="signup-submit-button"
					whileHover={submitting ? {} : { scale: 1.05 }}
					whileTap={submitting ? {} : { scale: 0.98 }}
					transition={{ duration: 0.2, ease: "easeOut" }}
				>
					<div
						aria-hidden="true"
						className="absolute border border-[#12301b] border-solid inset-0 pointer-events-none rounded-[2.25rem]"
					/>
					<div className="flex flex-row items-center justify-center size-full">
						<div className="box-border content-stretch flex gap-[0.5rem] h-[3.25rem] items-center justify-center px-[1.5rem] py-[1rem] relative w-full">
							{submitting ? (
								<>
									<Loader2 className="w-5 h-5 text-[#12301b] animate-spin" />
									<p
										className={`font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic relative shrink-0 text-[#12301b] text-nowrap tracking-[0.01rem] whitespace-pre ${styles.signupButtonText}`}
									>
										Creating account...
									</p>
								</>
							) : (
								<>
									<div
										className="overflow-clip relative shrink-0 size-[1.5rem]"
										data-name="Export icon"
									>
										<div
											className="absolute inset-[12.5%]"
											data-name="Icon"
										>
											<div
												className="absolute inset-[-5.56%]"
												style={
													{
														"--fill-0":
															"rgba(18, 48, 27, 1)",
														"--stroke-0":
															"rgba(18, 48, 27, 1)",
													} as React.CSSProperties
												}
											>
												<svg
													className="block size-full"
													fill="none"
													preserveAspectRatio="none"
													viewBox="0 0 20 20"
												>
													<path
														d={svgPaths.p3bdb3200}
														id="Icon"
														stroke="var(--stroke-0, black)"
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
													/>
												</svg>
											</div>
										</div>
									</div>
									<p
										className={`font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic relative shrink-0 text-[#12301b] text-nowrap tracking-[0.01rem] whitespace-pre ${styles.signupButtonText}`}
									>
										Sign up
									</p>
									<div
										className="overflow-clip relative shrink-0 size-[1.5rem]"
										data-name="Export icon"
									>
										<div
											className="absolute inset-[12.5%]"
											data-name="Icon"
										>
											<div
												className="absolute inset-[-5.56%]"
												style={
													{
														"--fill-0":
															"rgba(18, 48, 27, 1)",
														"--stroke-0":
															"rgba(18, 48, 27, 1)",
													} as React.CSSProperties
												}
											>
												<svg
													className="block size-full"
													fill="none"
													preserveAspectRatio="none"
													viewBox="0 0 20 20"
												>
													<path
														d={svgPaths.p3bdb3200}
														id="Icon"
														stroke="var(--stroke-0, black)"
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth="2"
													/>
												</svg>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</motion.button>
			</div>
		</form>
	);
}

function EmailSignUpSection({
	onOTPRequired,
}: {
	onOTPRequired: (email: string) => void;
}) {
	return (
		<div className="content-stretch flex flex-col gap-[1rem] items-start relative shrink-0 w-full">
			<Divider />
			<SignUpForm onOTPRequired={onOTPRequired} />
		</div>
	);
}

function SignUpContentArea({
	onOTPRequired,
}: {
	onOTPRequired: (email: string) => void;
}) {
	return (
		<div
			className="content-stretch flex flex-col gap-[1.5rem] items-center relative rounded-[0.75rem] shrink-0 w-full"
			data-name="Content"
		>
			<SocialButtons />
			<EmailSignUpSection onOTPRequired={onOTPRequired} />
		</div>
	);
}

function Row2() {
	return (
		<div
			className="content-stretch flex gap-[0.25rem] items-start justify-center relative shrink-0 w-full"
			data-name="Row"
			style={{ justifyContent: "center", alignItems: "center" }}
		>
			<p
				className={`font-['Inter:Regular',sans-serif] font-normal not-italic relative shrink-0 text-[#475467] text-nowrap whitespace-pre ${styles.signupLinkText}`}
			>
				Already have an account?
			</p>
			<Link
				href="/login"
				className="content-stretch flex gap-[0.5rem] items-center justify-center overflow-clip relative shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
				data-name="Button"
				data-testid="signup-login-button"
			>
				<p
					className={`font-['Inter:Semi_Bold',sans-serif] font-semibold not-italic relative shrink-0 text-[#2769af] text-nowrap whitespace-pre ${styles.signupLinkText}`}
				>
					Log in
				</p>
			</Link>
		</div>
	);
}

function RightSectionContent({
	onOTPRequired,
}: {
	onOTPRequired: (email: string) => void;
}) {
	return (
		<div
			className={`content-stretch flex flex-col gap-[2rem] items-start relative shrink-0 w-[25rem] ${styles.signupContainer}`}
			data-name="Content"
		>
			<SearchIconContainer />
			<TextAndSupportingText1 />
			<SignUpContentArea onOTPRequired={onOTPRequired} />
			<Row2 />
		</div>
	);
}

function Container({
	onOTPRequired,
}: {
	onOTPRequired: (email: string) => void;
}) {
	return (
		<div
			className="relative shrink-0 w-full"
			data-name="Container"
			style={{ marginTop: "auto", marginBottom: "auto" }}
		>
			<div className="flex flex-col items-center size-full">
				<div className="box-border content-stretch flex flex-col items-center px-[4rem] py-0 relative w-full">
					<RightSectionContent onOTPRequired={onOTPRequired} />
				</div>
			</div>
		</div>
	);
}

function SearchIconContainer() {
	return (
		<Link
			href="/"
			className="left-[4rem] size-[3rem] top-[3rem] cursor-pointer hover:opacity-70 transition-opacity"
			data-name="Search Icon Container"
			style={{ alignSelf: "flex-start" }}
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 48 48"
			>
				<g id="Search Icon Container">
					<rect
						height="47"
						rx="23.5"
						stroke="var(--stroke-0, #12301B)"
						width="47"
						x="0.5"
						y="0.5"
					/>
					<path
						d="M26 30L19 23.5L26 17"
						id="Icon"
						stroke="var(--stroke-0, #12301B)"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3.2"
					/>
				</g>
			</svg>
		</Link>
	);
}

function HelpIconContainer() {
	return (
		<Link
			href="/faq"
			className="fixed right-[2rem] bottom-[2rem] size-[3rem] bg-[#12301b] rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity shadow-lg z-50"
			data-name="Help Icon Container"
			aria-label="Help"
		>
			<div className="relative size-[1.5rem]">
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 21 19"
				>
					<path
						d={helpSvgPaths.p22f3cf00}
						id="Icon"
						stroke="white"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
					/>
				</svg>
			</div>
		</Link>
	);
}

function Section1({
	children,
	onOTPRequired,
}: {
	children?: React.ReactNode;
	onOTPRequired?: (email: string) => void;
}) {
	return (
		<div
			className="section-one basis-0 grow flex flex-col h-screen items-center justify-center relative shrink-0 overflow-y-auto"
			data-name="Section"
			style={{ flex: 0.5, justifyContent: "flex-start" }}
		>
			{children ||
				(onOTPRequired && <Container onOTPRequired={onOTPRequired} />)}
			{!children && (
				<p
					className={`font-['Inter:Regular',sans-serif] font-normal left-[7.15625rem] not-italic text-[#697b8e] text-center bottom-[2rem] ${styles.signupTermsText}`}
					style={{ width: "60%", paddingBottom: "2rem" }}
				>
					By signing up, you agree to our{" "}
					<Link
						href="/terms-of-service"
						className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline hover:text-[#475467] transition-colors cursor-pointer"
						data-testid="signup-terms-link"
					>
						Terms and Condition
					</Link>{" "}
					and{" "}
					<Link
						href="/privacy-policy"
						className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline hover:text-[#475467] transition-colors cursor-pointer"
						data-testid="signup-privacy-link"
					>
						Privacy policy
					</Link>
				</p>
			)}
		</div>
	);
}

export default function SignUpContent() {
	const [showOTP, setShowOTP] = useState(false);
	const [otpEmail, setOtpEmail] = useState("");
	const [otpError, setOtpError] = useState("");

	// Add auth-page class to html (and body for backward-compat) when component mounts, remove when unmounts
	useEffect(() => {
		if (typeof document !== "undefined") {
			document.documentElement.classList.add("auth-page");
			document.body.classList.add("auth-page");
		}
		return () => {
			if (typeof document !== "undefined") {
				document.documentElement.classList.remove("auth-page");
				document.body.classList.remove("auth-page");
			}
		};
	}, []);

	interface CognitoOTPError {
		code?: string;
		name?: string;
		message?: string;
	}

	const getOTPErrorMessage = (error: unknown): string => {
		const cognitoError = error as CognitoOTPError;
		const code = cognitoError?.code || cognitoError?.name;
		const message = cognitoError?.message || "";

		// Check if user is already confirmed - this is not really an error
		if (
			code === "NotAuthorizedException" &&
			(message.includes("already confirmed") ||
				message.includes("User is already confirmed"))
		) {
			return ""; // Return empty string - user is already confirmed, treat as success
		}

		switch (code) {
			case "CodeMismatchException":
				return "Invalid verification code. Please check the code and try again.";
			case "ExpiredCodeException":
				return "Verification code has expired. Please request a new code.";
			case "NotAuthorizedException":
				return "Invalid verification code. Please check the code and try again.";
			case "LimitExceededException":
				return "Too many attempts. Please try again later.";
			default:
				if (message) {
					if (
						message.includes("invalid") ||
						message.includes("mismatch") ||
						message.includes("incorrect")
					) {
						return "Invalid verification code. Please check the code and try again.";
					}
					if (message.includes("expired")) {
						return "Verification code has expired. Please request a new code.";
					}
					// Check for already confirmed message in default case too
					if (
						message.includes("already confirmed") ||
						message.includes("User is already confirmed")
					) {
						return ""; // Return empty string - treat as success
					}
				}
				return "Failed to verify code. Please try again.";
		}
	};

	const handleOTPRequired = (email: string) => {
		setOtpEmail(email);
		setOtpError("");
		setShowOTP(true);
	};

	const handleOTPConfirm = async (otp: string) => {
		try {
			setOtpError("");
			const { confirmUser } = await import("@/services/cognito");
			await confirmUser(otpEmail, otp);

			// Clear any previous errors on success
			setOtpError("");
			toast.success("Account confirmed successfully!");

			// Show loader before redirecting to onboarding
			// This loader will later be based on time to get user profile from database
			toast.info("Setting up your account...", {
				description: "Please wait while we prepare your onboarding",
			});

			// Simulate loading time (will be replaced with actual API call later)
			setTimeout(() => {
				window.location.href = "/user/onboard";
			}, 2000);
		} catch (error: unknown) {
			const errorMessage = getOTPErrorMessage(error);

			// If error message is empty (user already confirmed), treat as success
			if (errorMessage === "") {
				setOtpError("");
				toast.success("Account confirmed successfully!");
				setTimeout(() => {
					window.location.href = "/user/onboard";
				}, 2000);
				return;
			}

			// Only show error if it's a real error
			setOtpError(errorMessage);
			toast.error(errorMessage);
		}
	};

	const handleOTPResend = async () => {
		try {
			setOtpError("");
			const { resendSignup } = await import("@/services/cognito");
			await resendSignup({ username: otpEmail });
		} catch (error: unknown) {
			throw error;
		}
	};

	return (
		<div
			className="bg-white flex items-stretch relative w-full h-screen min-h-screen overflow-hidden"
			data-name="Sign up"
			style={{ justifyContent: "center" }}
		>
			<Section />
			<Section1 onOTPRequired={handleOTPRequired}>
				{showOTP ? (
					<div className="flex flex-col items-center justify-center w-full h-full px-4">
						<AnimatePresence mode="wait">
							<OTPInput
								key="otp"
								email={otpEmail}
								onSubmit={handleOTPConfirm}
								onResend={handleOTPResend}
								cooldownScope="signup"
								title="Verify Your Email"
								description={`Enter the 6-digit code sent to ${otpEmail}`}
								errorMessage={otpError}
							/>
						</AnimatePresence>
					</div>
				) : null}
			</Section1>
			<HelpIconContainer />
		</div>
	);
}
